"use client";

import React from "react";
import { motion, easeOut } from "framer-motion";
import { useInView } from "react-intersection-observer";
import QuoteDouble from "@/components/icons/QuoteDouble";

type Testimonial = {
  quote: string;
  name: string;
  role: string;
};

const testimonials: Testimonial[] = [
  {
    quote:
      "The atmosphere felt very Arabic. The quality of the food was very good. Also, the behavior of all the staff is very good.",
    name: "M Jasim Nahid",
    role: "Valuable Guest",
  },
  {
    quote:
      "Good Food, Good Environment for Family and Friend. Recommending AlMajlis Restaurant.",
    name: "Nasib Khan",
    role: "Valuable Guest",
  },
  {
    quote:
      "The only real Arabic restaurant in Bangladesh. Great food! Good salad.",
    name: "Ahmed Alsakkaf",
    role: "Valuable Guest",
  },
];

const TestimonialsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.18, delayChildren: 0.25 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: easeOut },
    },
  };

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      className=" p-[30px] sm:p-10 2xl:py-[120px] 2xl:px-[260px]"
      id="testimonials"
    >
      {/* Heading */}
      <motion.div
        variants={containerVariants}
        className="text-center max-w-4xl mx-auto"
      >
        <motion.h2
          variants={itemVariants}
          className="font-amiri font-bold text-white text-[30px] sm:text-[48px] leading-[1.05]"
        >
          What Our Guests Say About <br className="hidden sm:block" />
          AlMajlis
        </motion.h2>

        <motion.p
          variants={itemVariants}
          className="mt-3 sm:mt-4 text-white/60 text-sm sm:text-base"
        >
          Real stories, real flavors — discover why diners keep coming back to
          AlMajlis for authentic taste and warm hospitality.
        </motion.p>
      </motion.div>

      {/* Cards */}
      <motion.div
        variants={containerVariants}
        className="mt-8 sm:mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 sm:gap-8"
      >
        {testimonials.map((t, i) => (
          <motion.article
            key={i}
            variants={itemVariants}
            className="relative rounded-2xl border border-[#C5A152]/60 bg-white/0"
          >
            <div className="p-6 sm:p-8">
              {/* avatar circle */}
              <div className="w-12 h-12 rounded-full bg-white border border-white/20 grid place-items-center">
                {/* quote-bubble dot just for accent */}
                <span className="block w-3 h-3 rounded-full bg-white/60" />
              </div>

              {/* quote icon */}
              <h5 className="pt-5">
                {" "}
                <QuoteDouble />
              </h5>

              <p className="mt-3 text-[15px] leading-relaxed text-white/90">
                {t.quote}
              </p>

              {/* divider */}
              <div className="mt-6 h-px w-full bg-white/10" />

              {/* person */}
              <div className="mt-4">
                <p className="text-old-lace font-medium">{t.name}</p>
                <p className="text-xs text-white/70">{t.role}</p>
              </div>
            </div>

            {/* thin outer frame like the mock */}
            <span className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-[#C5A152]/40" />
          </motion.article>
        ))}
      </motion.div>
    </motion.section>
  );
};

export default TestimonialsSection;
